import { useEffect } from "react";
import { X } from "lucide-react";

/**
 * A generic dark "intercepted transmission" overlay used by several anomalies.
 * Escape closes; focus is moved to the close button.
 */
export const SecretOverlay = ({
  open,
  onClose,
  eyebrow,
  title,
  children,
  footer,
  testId = "secret-overlay"
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-start justify-center overflow-y-auto bg-[hsl(220_20%_3%/0.92)] px-4 py-10 backdrop-blur-sm sm:py-16"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid={testId}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="panel w-full max-w-2xl overflow-hidden">
        <div className="flex items-start justify-between gap-4 border-b border-border bg-[hsl(220_18%_8%)] px-4 py-3 sm:px-6">
          <p className="font-mono text-[10px] uppercase leading-relaxed tracking-[0.18em] text-[hsl(32_80%_66%)]">
            {eyebrow}
          </p>
          <button
            type="button"
            onClick={onClose}
            autoFocus
            aria-label="Close"
            data-testid="secret-overlay-close"
            className="-mr-1 -mt-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] text-muted-foreground transition-colors duration-150 hover:bg-white/5 hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-4 py-6 sm:px-6 sm:py-8">
          <h2 className="text-2xl font-semibold leading-tight sm:text-3xl">
            {title}
          </h2>
          <div className="mt-5 space-y-4">{children}</div>
        </div>

        {footer && (
          <div className="border-t border-border bg-[hsl(220_18%_8%)] px-4 py-3 sm:px-6">
            <p className="font-mono text-[10px] uppercase leading-relaxed tracking-[0.16em] text-[hsl(215_10%_52%)]">
              {footer}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
